<template>
  <div>
    <SkeleLoader
      v-if="isLoading"
      :lines="3"
      imgSize="50px"
      contentIsFullWidth
    />
    <PaymentFormSelect
      v-else
      :key="orderStore.form.mode"
      name="payment"
      :label="label"
      :items="items"
      :defaultItem="defaultPaymentMethod"
      :hideDescription="hideDescription"
      @dropdownValueUpdate="updateDropdown"
    />
  </div>
  <!-- </form> -->
</template>

<script setup>
// ====================================
// Packages
// ====================================
import { ref, onMounted, watch } from "vue";
import { whenever, watchOnce } from "@vueuse/core";

// ====================================
// Storage
// ====================================
import { useOrderFormStore } from "@/stores/forms/orderFormStore";
// ====================================
// Components
// ====================================
import PaymentFormSelect from "@/components/ui/selects/PaymentFormSelect.vue";
import SkeleLoader from "@/components/ui/loaders/SkeleLoader.vue";

const orderStore = useOrderFormStore();

const props = defineProps({
  items: { type: Array, default: () => [] },
  defaultsSetFromUrl: {
    type: Boolean,
    default: false,
  },
  label: {
    type: String,
    default: "Payment method"
  },
  hideDescription: {
    type: Boolean,
    default: false,
  }
});

const emit = defineEmits({
  inputUpdated: (payment) => typeof payment === "string",
});

const isLoading = ref(true);
const defaultPaymentMethod = ref("");

const updateOrderWithPayment = (payment) => {
  if (!payment?.code) {
    return;
  }

  emit("inputUpdated", { payment: payment.code.toLowerCase() });

  orderStore.setActiveModePaymentProvider(payment.code.toLowerCase());
  orderStore.setPspTypeCode(payment.paymentType.toLowerCase());
  orderStore.setPspRequireMobile(payment.require_mobile);
};

const runOnMounted = (paymentOnMount) => {
  if (!paymentOnMount) {
    return;
  }

  isLoading.value = false;
  updateOrderWithPayment(paymentOnMount);
};

whenever(
  () =>
    orderStore.getActiveStore.form.coinType.value &&
    props.items.length > 0 &&
    !orderStore.isOrderFromMerchantApi &&
    orderStore.getActiveStorePaymentData.length > 0, // added
  () => {
    const paymentOnMount = orderStore.filterPaymentsByFiatAndCoin[0];
    if (paymentOnMount){
      defaultPaymentMethod.value = paymentOnMount;
    }
    runOnMounted(paymentOnMount);
  },
  { deep: true, immediate: true }
);

const getDefaultPayment = (currentPayment, validPSPList) => {
  if (validPSPList.length === 0){
    return null;
  }
  if (!currentPayment){
    return validPSPList[0];
  }
  const filteredValidPSPList = validPSPList.filter(payment => payment.code === currentPayment);
  return filteredValidPSPList.length > 0 ? filteredValidPSPList[0] : validPSPList[0];
}

// Watch for B2B order
watch(
  () =>
    orderStore.isOrderFromMerchantApi &&
    orderStore.filterPaymentsByFiatAndCoin.length &&
    orderStore.getActiveStorePaymentData.length > 0, // added
  () => {
    const paymentOnMount = getDefaultPayment(orderStore.getMerchantApiData.payment, orderStore.filterPaymentsByFiatAndCoin);
    if (paymentOnMount) {
      defaultPaymentMethod.value = paymentOnMount;
    }
    runOnMounted(paymentOnMount);
  },
  { deep: true, immediate: true }
);

const updateDropdown = (item) => {
  updateOrderWithPayment(item.payment);
};
</script>
<style lang="scss">
.form {
  .bodycopy {
    margin-bottom: 1rem;
    @include breakpoint($min-m) {
      margin-bottom: 1.5rem;
    }
  }
  &__payment-view {
    min-height: auto;
    min-width: auto;
    position: relative;
    border-radius: $border-radius;
    overflow: hidden;
  }

  .dropdown-select #paymentItem {
    padding-left: 1rem;
  }
}
</style>
